<template>
	<div class="two-slot-offer-detail template-layout">
		<OfferHeaderBlock
			:contentData="contentData"
			@closePopup="$emit('closePopup', $event)"
		/>
		<OfferRibbonBlock :contentData="contentData" />
		<OfferContentBlock
			:skin="contentData.attributes?.skin"
			:subtype="contentData.attributes?.subtype"
		>
			<template #content>
				<div
					v-if="firstSlotItems"
					class="two-slot-offer-detail__first-slot"
				>
					<BuildingRewardsGrid
						v-if="firstSlotGroup === CommodityGroup.BUILDING"
						:items="firstSlotItems as BuildingSlotItem[]"
						:context="RewardsGridContext.POPUP"
						:template="contentData.attributes.template"
					/>
					<UpgradeRewardsGrid
						v-if="firstSlotGroup === CommodityGroup.UPGRADE"
						:items="firstSlotItems as UpgradeSlotItem[]"
						:context="RewardsGridContext.POPUP"
						:template="contentData.attributes.template"
					/>
					<ResourceOrUtilityRewardsGrid
						v-if="
							firstSlotGroup === CommodityGroup.RESOURCE ||
							firstSlotGroup === CommodityGroup.UTILITY
						"
						:items="firstSlotItems as ResourceOrUtilitySlotItem[]"
						:context="RewardsGridContext.POPUP"
					/>
				</div>

				<DecorativeTextDivider
					label="+"
					color="#f4d278"
					:fontSize="96"
					:textGap="20"
					:style="{
						marginBottom:
							secondSlotGroup === CommodityGroup.EVENT_RESOURCE
								? '2rem'
								: '1rem',
					}"
				/>

				<div
					v-if="
						secondSlotItems &&
						secondSlotGroup !== CommodityGroup.EVENT_RESOURCE
					"
					class="two-slot-offer-detail__second-slot"
				>
					<ResourceOrUtilityRewardsGroup
						v-if="
							secondSlotGroup === CommodityGroup.RESOURCE ||
							secondSlotGroup === CommodityGroup.UTILITY
						"
						:items="secondSlotItems as ResourceOrUtilitySlotItem[]"
						:context="RewardsGridContext.POPUP"
						isBordered
					/>
				</div>
			</template>
		</OfferContentBlock>
		<ResourceOrUtilityRewardsGroup
			v-if="secondSlotGroup === CommodityGroup.EVENT_RESOURCE"
			:items="secondSlotItems as EventResourceSlotItem[]"
			:context="RewardsGridContext.POPUP"
			:isEvent="secondSlotGroup === CommodityGroup.EVENT_RESOURCE"
		/>
	</div>
</template>

<script lang="ts" setup>
import OfferContentBlock from "@/components/shop/blocks/OfferContentBlock.vue";
import OfferHeaderBlock from "@/components/shop/blocks/OfferHeaderBlock.vue";
import OfferRibbonBlock from "@/components/shop/blocks/OfferRibbonBlock.vue";
import BuildingRewardsGrid from "@/components/shop/blocks/parts/BuildingRewardsGrid.vue";
import ResourceOrUtilityRewardsGrid from "@/components/shop/blocks/parts/ResourceOrUtilityRewardsGrid.vue";
import ResourceOrUtilityRewardsGroup from "@/components/shop/blocks/parts/ResourceOrUtilityRewardsGroup.vue";
import DecorativeTextDivider from "@/components/SVG/DecorativeTextDivider.vue";

import type {
	BuildingSlotItem,
	EventResourceSlotItem,
	ResourceOrUtilitySlotItem,
	UpgradeSlotItem,
} from "@/types/shop";
import { CommodityGroup, type ShopItem } from "@/types/shop";
import { RewardsGridContext } from "@/types/ui";
import { computed } from "vue";
import UpgradeRewardsGrid from "../blocks/parts/UpgradeRewardsGrid.vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const firstSlotGroup = computed((): CommodityGroup | null => {
	const firstSlot = contentData.attributes?.slots?.[0];
	const firstItem = firstSlot?.items?.[0];
	return firstItem?.group ?? null;
});

const secondSlotGroup = computed((): CommodityGroup | null => {
	const secondSlot = contentData.attributes?.slots?.[1];
	const secondItem = secondSlot?.items?.[0];
	return secondItem?.group ?? null;
});

const firstSlotItems = computed(
	():
		| BuildingSlotItem[]
		| UpgradeSlotItem[]
		| ResourceOrUtilitySlotItem[] => {
		return (
			(contentData.attributes?.slots?.[0]?.items as
				| BuildingSlotItem[]
				| UpgradeSlotItem[]
				| ResourceOrUtilitySlotItem[]) ?? null
		);
	},
);

const secondSlotItems = computed(
	(): ResourceOrUtilitySlotItem[] | EventResourceSlotItem[] => {
		return (
			(contentData.attributes?.slots?.[1]?.items as
				| ResourceOrUtilitySlotItem[]
				| EventResourceSlotItem[]) ?? null
		);
	},
);
</script>

<style lang="scss" scoped></style>
